/**
 * PublicStatementPage — publicly accessible accessibility statement.
 * Route: /statement/:websiteId (no auth required)
 */

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { apiGet } from "../api/client.js";
import type { PublicStatement } from "../api/types.js";

interface PublicStatementResponse {
  statement: PublicStatement;
}

function fetchPublicStatement(
  websiteId: string,
): Promise<PublicStatementResponse> {
  return apiGet<PublicStatementResponse>(`/public/statement/${websiteId}`);
}

export function PublicStatementPage() {
  const { websiteId } = useParams<{ websiteId: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["public-statement", websiteId],
    queryFn: () => fetchPublicStatement(websiteId!),
    enabled: websiteId != null,
    retry: false,
  });

  const statement = data?.statement;

  if (isLoading) {
    return (
      <div className="public-statement">
        <div className="public-statement__inner">
          <div className="loading-text">Loading accessibility statement…</div>
        </div>
      </div>
    );
  }

  if (isError || !statement) {
    return (
      <div className="public-statement">
        <div className="public-statement__inner">
          <h1>Accessibility statement not available</h1>
          <p className="text-muted">
            {error instanceof Error
              ? error.message
              : "This accessibility statement could not be found or has not been published yet."}
          </p>
        </div>
      </div>
    );
  }

  const scanDate = statement.completed_at ?? statement.generated_at;
  const formattedDate = new Date(scanDate).toLocaleDateString("en-IE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="public-statement">
      <div className="public-statement__inner">
        <header className="public-statement__header">
          <h1 className="public-statement__title">Accessibility Statement</h1>
          {statement.org_name && (
            <p className="public-statement__org">{statement.org_name}</p>
          )}
          <p className="public-statement__url">
            <a
              href={statement.website_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {statement.website_url}
            </a>
          </p>
        </header>

        <section
          className="public-statement__meta"
          aria-label="Statement details"
        >
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-item__value">{statement.wcag_level}</span>
              <span className="stat-item__label">WCAG standard</span>
            </div>
            {statement.score_value != null && (
              <div className="stat-item">
                <span className="stat-item__value">
                  {Math.round(statement.score_value)}/100
                </span>
                <span className="stat-item__label">Automated scan score</span>
              </div>
            )}
            <div className="stat-item">
              <span className="stat-item__value">
                {statement.pages_scanned}
              </span>
              <span className="stat-item__label">Pages scanned</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{formattedDate}</span>
              <span className="stat-item__label">Last assessed</span>
            </div>
          </div>
        </section>

        {statement.statement_html ? (
          <section
            className="public-statement__body"
            aria-label="Accessibility statement content"
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(statement.statement_html, {
                ALLOWED_TAGS: [
                  "p",
                  "h1",
                  "h2",
                  "h3",
                  "strong",
                  "em",
                  "a",
                  "ul",
                  "ol",
                  "li",
                  "table",
                  "thead",
                  "tbody",
                  "tr",
                  "th",
                  "td",
                  "br",
                  "hr",
                ],
                ALLOWED_ATTR: ["href", "target", "rel", "title"],
              }),
            }}
          />
        ) : (
          <section className="public-statement__body">
            <p className="text-muted">
              Full statement text is not yet available.
            </p>
          </section>
        )}

        <footer className="public-statement__footer">
          <p className="text-muted text-sm" style={{ marginBottom: "0.5rem" }}>
            This statement was generated using automated accessibility scanning
            tools. Automated testing cannot detect all accessibility issues —
            manual expert review is recommended for a complete assessment.
          </p>
          <p className="text-muted text-sm" style={{ marginBottom: "0.75rem" }}>
            Assessment date: {formattedDate}. Results may no longer reflect the
            current state of the website if changes have been made since this
            date.
          </p>
          <p className="public-statement__badge">
            <Link to="/" className="text-muted text-sm">
              Powered by AccessiMonitor
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
